/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { Dispatch } from 'react';
import { loadUniversalTrackerModal } from '../../../../../actions/universalTrackerModal';
import { CurrentUserData } from '../../../../../reducers/current-user';
import { SurveyPermissions } from '../../../../../services/permissions/SurveyPermissions';
import { AssuranceButton } from '../../../../assurance/assurance-button';
import DelegationModal from '../../../../delegation-modal';
import CompositeValueHistory from '../../../../value-history/CompositeValueHistory';
import { QuestionMenuProps } from '../../QuestionMenu';
import { ToolbarButtonProps } from '../ToolbarButton';
import { UniversalTrackerType } from '../../../../../model/UniversalTracker';
import { QUESTION } from '../../../../../constants/terminology';

interface ActionMenuItemProps extends QuestionMenuProps {
  dispatch: Dispatch<any>;
  canAccessAssurance: boolean;
  user: CurrentUserData;
}

interface MenuItemButtonProps extends ToolbarButtonProps {
  id: ModalView;
}

export enum ModalView {
  Delegation = 'delegation',
  History = 'history',
  Provenance = 'provenance',
  Assurance = 'assurance',
}

export function getMenuItems(props: ActionMenuItemProps): MenuItemButtonProps[] {
  const {
    utrv,
    survey,
    handleRefreshQuestion,
    isCompleted,
    canAccessAssurance,
    dispatch,
    user,
    alternativeCode,
  } = props;

  const canAccessAllData = SurveyPermissions.canAccessAllData(survey, user);

  const isReadOnly = Boolean(survey.completedDate);

  const items: MenuItemButtonProps[] = [];
  items.push({
    id: ModalView.Delegation,
    contents: (
      <>
        <i className='fas fa-user-plus text-ThemeAccentMedium mr-2' />
        Delegation
      </>
    ),
    tooltip: `View/Edit this ${QUESTION.SINGULAR}'s contributors and verifiers`,
    disabled: isCompleted || !canAccessAllData,
    modal: {
      component: DelegationModal,
      utrvs: [utrv],
      handleSubmit: handleRefreshQuestion,
      survey: props.survey,
      initiativeId: props.survey.initiativeId,
    },
  });
  items.push({
    id: ModalView.History,
    contents: (
      <>
        <i className='fas fa-history text-ThemeAccentMedium mr-2' />
        Data History
      </>
    ),
    tooltip: `View this ${QUESTION.SINGULAR}'s data history`,
    onClick: () =>
      dispatch(
        loadUniversalTrackerModal({
          universalTrackerId: utrv.universalTrackerId,
          universalTrackerType: UniversalTrackerType.Utr,
          actionValueId: utrv._id,
          params: { surveyId: survey._id },
          alternativeCode,
        })
      ),
  });
  items.push({
    id: ModalView.Provenance,
    contents: (
      <>
        <i className='fas fa-clipboard text-ThemeAccentMedium mr-2' />
        Data Provenance
      </>
    ),
    tooltip: `View this ${QUESTION.SINGULAR}'s data provenance`,
    modal: {
      component: CompositeValueHistory,
      isReadOnly: isReadOnly,
      isModal: true,
      ids: [utrv._id],
    },
  });

  const { text, onClick } = AssuranceButton(props);
  if (text && canAccessAssurance) {
    items.push({
      id: ModalView.Assurance,
      contents: (
        <>
          <i className={'fa fa-award assurance-btn text-ThemeAccentMedium mr-2'} />
          Assurance
        </>
      ),
      disabled: !canAccessAllData,
      tooltip: text,
      onClick,
    });
  }
  return items;
}
